import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { Download, FileText } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useSupplierDocumentsAtCutoff } from "~/hooks/purchasing";
import { usePagination } from "~/hooks/usePagination";
import { exportSupplierAgingPorCodigo } from "~/pages/purchasing/utils/exportSupplierAgingReports";
import { formatCurrency } from "~/utils/helpers";

import {
  type AgingRow,
  exportCuentasPorPagarResumen,
} from "./utils/exportCuentasPorPagar";

const clp = (n: number) => `$${formatCurrency(n)}`;

const ALL_SUPPLIERS = "all";

export function CuentasPorPagarPage() {
  const { data: companies = [] } = useCompanies();
  const [companyId, setCompanyId] = useState<string>(ALIACE_SPA_ID);
  // This is a historical AP snapshot, so open on the last closed month-end (last day of the
  // previous month) instead of today — otherwise it shows today's live balance by default.
  const [cutoff, setCutoff] = useState(() =>
    dayjs().startOf("month").subtract(1, "day").format("YYYY-MM-DD")
  );
  // Optional report filters: a single proveedor, and an emission-date range (desde / hasta).
  const [supplierId, setSupplierId] = useState<string>(ALL_SUPPLIERS);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data: docs = [], isLoading } = useSupplierDocumentsAtCutoff(
    cutoff,
    true,
    companyId
  );

  // Proveedor options for the filter — every supplier with open documents at the cutoff.
  const supplierOptions = useMemo<SearchableSelectOption[]>(() => {
    const seen = new Map<string, string>();
    for (const d of docs) {
      if (!seen.has(d.supplier_id)) seen.set(d.supplier_id, d.nombre);
    }
    const opts = [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: ALL_SUPPLIERS, label: "Todos los proveedores" }, ...opts];
  }, [docs]);

  // Apply the proveedor + emission-range filters (dates compare lexicographically on YYYY-MM-DD).
  const filteredDocs = useMemo(() => {
    return docs.filter((d) => {
      if (supplierId !== ALL_SUPPLIERS && d.supplier_id !== supplierId) {
        return false;
      }
      if (fromDate && (!d.emission_date || d.emission_date < fromDate)) {
        return false;
      }
      if (toDate && (!d.emission_date || d.emission_date > toDate)) {
        return false;
      }
      return true;
    });
  }, [docs, supplierId, fromDate, toDate]);

  // Aggregate per supplier into aging buckets relative to the cutoff.
  const rows = useMemo<AgingRow[]>(() => {
    const map = new Map<string, AgingRow>();
    for (const d of filteredDocs) {
      let r = map.get(d.supplier_id);
      if (!r) {
        r = {
          supplier_id: d.supplier_id,
          softland_code: d.softland_code,
          nombre: d.nombre,
          porVencer: 0,
          b1: 0,
          b2: 0,
          b3: 0,
          b4: 0,
          total: 0,
        };
        map.set(d.supplier_id, r);
      }
      const amt = Number(d.saldo_at);
      r.total += amt;
      if (d.estado !== "vencida") {
        r.porVencer += amt;
      } else {
        const days = dayjs(cutoff).diff(dayjs(d.due_date), "day");
        if (days <= 30) r.b1 += amt;
        else if (days <= 60) r.b2 += amt;
        else if (days <= 90) r.b3 += amt;
        else r.b4 += amt;
      }
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [filteredDocs, cutoff]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          porVencer: acc.porVencer + r.porVencer,
          b1: acc.b1 + r.b1,
          b2: acc.b2 + r.b2,
          b3: acc.b3 + r.b3,
          b4: acc.b4 + r.b4,
          total: acc.total + r.total,
        }),
        { porVencer: 0, b1: 0, b2: 0, b3: 0, b4: 0, total: 0 }
      ),
    [rows]
  );
  const vencido = totals.b1 + totals.b2 + totals.b3 + totals.b4;

  const pagination = usePagination(rows, 20);

  const companyName =
    companies.find((c) => c.id === companyId)?.name ?? "Empresa";
  const selectedSupplierName =
    supplierId === ALL_SUPPLIERS
      ? undefined
      : supplierOptions.find((o) => o.value === supplierId)?.label;

  const exportMeta = {
    empresa: companyName,
    cutoff,
    from: fromDate || undefined,
    to: toDate || undefined,
    proveedor: selectedSupplierName,
  };

  const handleExportResumen = () => {
    exportCuentasPorPagarResumen(rows, totals, exportMeta);
  };

  const handleExportDetalle = () => {
    // Group by proveedor so the "por código" subtotals land correctly, then alphabetical.
    const detailDocs = [...filteredDocs].sort(
      (a, b) =>
        a.nombre.localeCompare(b.nombre) ||
        a.supplier_id.localeCompare(b.supplier_id) ||
        (a.emission_date ?? "").localeCompare(b.emission_date ?? "")
    );
    exportSupplierAgingPorCodigo(detailDocs, cutoff);
  };

  const filters = (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="grid gap-1.5">
        <Label htmlFor="cxp-empresa">Empresa</Label>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger id="cxp-empresa" className="h-9 w-56">
            <SelectValue placeholder="Empresa" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cxp-corte">Fecha de corte</Label>
        <Input
          id="cxp-corte"
          type="date"
          value={cutoff}
          onChange={(e) => setCutoff(e.target.value)}
          className="h-9 w-44"
        />
      </div>
      <div className="grid gap-1.5">
        <Label>Proveedor</Label>
        <SearchableSelect
          value={supplierId}
          onValueChange={setSupplierId}
          options={supplierOptions}
          placeholder="Todos los proveedores"
          searchPlaceholder="Buscar proveedor..."
          className="w-64"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cxp-desde">Emisión desde</Label>
        <Input
          id="cxp-desde"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="h-9 w-44"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cxp-hasta">Emisión hasta</Label>
        <Input
          id="cxp-hasta"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="h-9 w-44"
        />
      </div>
    </div>
  );

  const exportAction = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={rows.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Reporte de proveedores</DropdownMenuLabel>
        <DropdownMenuItem onClick={handleExportResumen}>
          Resumen por proveedor
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportDetalle}>
          Detalle de documentos
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {selectedSupplierName
            ? `Proveedor: ${selectedSupplierName}`
            : "Todos los proveedores"}
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <PageContainer>
      <PageHeader
        title="Cuentas por Pagar"
        description="Saldo pendiente a proveedores por antigüedad, por empresa"
        actions={exportAction}
      />

      <Card>
        <CardContent className="p-4">{filters}</CardContent>
      </Card>

      {isLoading ? (
        <TableSkeleton rows={8} columns={7} />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={FileText}
              title="Sin saldos pendientes"
              description="No hay documentos por pagar para esta empresa al corte y filtros seleccionados."
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total por pagar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{clp(totals.total)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Vencido
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-red-700">{clp(vencido)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Por vencer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{clp(totals.porVencer)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Proveedores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{rows.length}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="text-right">Por vencer</TableHead>
                    <TableHead className="text-right">1–30</TableHead>
                    <TableHead className="text-right">31–60</TableHead>
                    <TableHead className="text-right">61–90</TableHead>
                    <TableHead className="text-right">90+</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagination.pageItems.map((r) => (
                    <TableRow key={r.supplier_id}>
                      <TableCell className="font-medium text-gray-800">
                        {r.nombre}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.porVencer ? clp(r.porVencer) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.b1 ? clp(r.b1) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.b2 ? clp(r.b2) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.b3 ? clp(r.b3) : "-"}
                      </TableCell>
                      <TableCell className="text-right text-red-700">
                        {r.b4 ? clp(r.b4) : "-"}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {clp(r.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-semibold">Totales</TableCell>
                    <TableCell className="text-right font-semibold">
                      {clp(totals.porVencer)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {clp(totals.b1)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {clp(totals.b2)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {clp(totals.b3)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-red-700">
                      {clp(totals.b4)}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {clp(totals.total)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
              <div className="border-t p-4">
                <TablePagination
                  page={pagination.page}
                  pageCount={pagination.pageCount}
                  totalItems={pagination.total}
                  pageSize={20}
                  onPageChange={pagination.setPage}
                  itemLabel="proveedores"
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </PageContainer>
  );
}
